import './TanStackTable.css'
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel
} from "@tanstack/react-table"
import React, { useContext, useState } from "react"
import TableHeader from '../TableHeader/TableHeader'
import TableContent from '../TableContent/TableContent'
import TablePagination from '../TablePagination/TablePagination'
import TableNoContet from '../TableNoContent/TableNoContet'
import { Skeleton } from '@mantine/core'
import showNotification from "../../../utils/showNotification";
import axios from "axios";
import AuthContext from "../../../contexts/AuthContext";
import { flattenObject } from "../../../utils/flattenObject";

export default function TanStackTable({
    children,
    data,
    columns,
    withInputSearch = true,
    withPagination = true,
    title,
    headerChildren,
    editFunc,
    onDelete,
    actionsFunc = null,
    addButtonFunc = null,
    addButtonText = 'Crear',
    loading,
    canRezise = false,
    onLoadTable,
    displayForm,
    initialValues = {
        pageSize: 10
    }
}) {

    const { authToken } = useContext(AuthContext)
    const [sorting, setSorting] = useState([])
    const [columnFilters, setColumnFilters] = useState([])

    const table = useReactTable({
        columns,
        data,
        columnResizeMode: canRezise ? 'onChange' : null,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        defaultColumn: {
            size: 200, //starting column size
            minSize: 50, //enforced during column resizing
            maxSize: 400, //enforced during column resizing
        },
    })

    if (onLoadTable) {
        onLoadTable(table)
    }

    const downloadCSV = async (_res) => {
        const url = window.URL.createObjectURL(new Blob([_res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "export.csv");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const handleExport = async () => {
        const filteredRows = table.getFilteredRowModel().flatRows.map(row => row.original);

        if (!filteredRows || filteredRows.length === 0) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error al descargar archivo CSV",
                message: `Detalle: No se han encontrado datos para exportar.`
            });
            return;
        }

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            responseType: 'blob'
        };

        // CAMBIO CLAVE: Recolectar TODAS las columnas de TODOS los objetos
        const allColumns = new Set();
        const flattenData = filteredRows.map((row) => {
            const flattened = flattenObject(row);
            // Agregar todas las claves de este objeto al Set
            Object.keys(flattened).forEach(key => allColumns.add(key));
            return flattened;
        });

        // Convertir el Set a Array para tener todas las columnas únicas
        const columns = Array.from(allColumns);

        // Asegurar que todos los objetos tengan todas las columnas (con valores vacíos si no existen)
        const normalizedData = flattenData.map(row => {
            const normalizedRow = {};
            columns.forEach(col => {
                normalizedRow[col] = row[col] !== undefined ? row[col] : '';
            });
            return normalizedRow;
        });

        const jsonData = {
            data: normalizedData,
            columns: columns,
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}admin/exportCSV`,
                jsonData,
                config
            );
            await downloadCSV(response);
            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: `El CSV se ha exportado correctamente con ${normalizedData.length} filas.`,
            });
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error al exportar CSV",
                message: `Detalle: ${error.message}`
            });
        }
    };

    if (!data) return < TableNoContet />
    return (
        <div className='TanStackTable-first-container' style={{ display: displayForm ? "none" : "block" }}>
            <div className='TanStackTable-wrapper-container'>
                {withInputSearch &&
                    <TableHeader TableHeader
                        title={title}
                        addButtonFunc={addButtonFunc}
                        addButtonText={addButtonText}
                        handleExport={handleExport}
                    >{headerChildren}</TableHeader>
                }

                <div className='TanStackTable-TableContent-wrapper'>
                    {loading
                        ? < Skeleton h='450px' w='100%' />
                        : < TableContent
                            table={table}
                            editFunc={editFunc}
                            onDelete={onDelete}
                            actionsFunc={actionsFunc}
                        />
                    }
                </div>

            </div>

            {withPagination &&
                < TablePagination
                    table={table}
                    initialValues={initialValues}
                />
            }

            {children}
        </div>
    )
}
